import Vue from 'vue'
import axios from 'axios'
import router from '@/router'

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname)

let instance

/** Returns the current instance of the SDK */
export const getInstance = () => instance

/** Creates an instance of the Session Auth wrapper. If one has already been created, it returns that instance */
export const useSessionAuth = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  store = null
} = {}) => {
  if (instance) return instance

  // The 'instance' is simply a Vue object (mimics Auth0Plugin structure)
  instance = new Vue({
    data () {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        popupOpen: false,
        error: null,
        vuexStore: store // Store reference to Vuex store
      }
    },
    methods: {
      /** Logs in with email/password and redirects to dashboard */
      async loginWithCredentials (email, password) {
        try {
          const response = await axios.post('/api/auth/login', {
            email,
            password
          }, {
            withCredentials: true
          })

          if (response.data.success) {
            this.user = response.data.user
            this.isAuthenticated = true
            this.error = null

            // Show global loading overlay IMMEDIATELY before redirect
            if (this.vuexStore) {
              this.vuexStore.dispatch('application/setGlobalLoading', {
                show: true,
                message: 'Logging you in...'
              })
            }

            // CRITICAL: Update Vuex BEFORE redirecting to ensure RLS permissions are set
            const homePath = (response.data.user.homePath === 'PatientDashboard' ? 'MyDashboard' : response.data.user.homePath) || 'ClinicHome'
            console.log('homePath:', homePath)
            if (this.vuexStore) {
              await this.vuexStore.dispatch('user/setUser', this.user)
            } else {
              console.error('[SessionAuth] ERROR: vuexStore is not available!')
            }

            // Redirect to user's home path using Vue Router
            // homePath from database is a route NAME (e.g., "OvbDashboard"), not a path
            const resolved = router.resolve({ name: homePath })
            console.log(resolved)
            if (resolved && resolved.route && resolved.route.path !== '/') {
              router.push(resolved.route.path).catch(routeErr => {
                console.warn('[SessionAuth] Route push failed, going to /:', routeErr)
                router.push('/')
              })
            } else {
              console.warn('[SessionAuth] Could not resolve route name:', homePath, '- going to /')
              router.push('/')
            }

            // Keep overlay visible for 1 second to prevent UI flashing
            setTimeout(() => {
              if (this.vuexStore) {
                this.vuexStore.dispatch('application/setGlobalLoading', {
                  show: false,
                  message: ''
                })
              }
            }, 1000)

            // Fire-and-forget: send browser/device info to server
            try {
              const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
              axios.post('/api/auth/system-info', {
                screenWidth: screen.width,
                screenHeight: screen.height,
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
                devicePixelRatio: window.devicePixelRatio,
                colorDepth: screen.colorDepth,
                touchPoints: navigator.maxTouchPoints || 0,
                language: navigator.language,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                cookiesEnabled: navigator.cookieEnabled,
                online: navigator.onLine,
                connectionType: conn ? conn.effectiveType : null
              }, { withCredentials: true }).catch(() => {})
            } catch (e) {
              // Silently ignore — system info is non-critical
            }

            return response.data
          }
        } catch (e) {
          this.error = e.response?.data?.error || 'Login failed'
          throw e
        }
      },

      /** Logs the user out */
      async logout () {
        // Show global loading overlay IMMEDIATELY
        if (this.vuexStore) {
          this.vuexStore.dispatch('application/setGlobalLoading', {
            show: true,
            message: 'Logging you out...'
          })
        }

        try {
          // Call logout endpoint to destroy session
          await axios.get('/logout', { withCredentials: true })
        } catch (e) {
          this.error = e
        }

        this.user = {}
        this.isAuthenticated = false
        this.error = null

        // Clear Vuex state so persisted store doesn't restore stale data
        if (this.vuexStore) {
          this.vuexStore.dispatch('user/logout')
          this.vuexStore.dispatch('app/logout')
          this.vuexStore.dispatch('RESET_OVB')
        }

        setTimeout(() => {
          window.location.href = '/?logout=success'
        }, 1500)
      },

      /** Gets the current user from session */
      async getUser () {
        return this.user
      }
    },

    /** Use this lifecycle method to check if user is authenticated (mimics Auth0's created hook) */
    async created () {
      try {
        // Always check the real session — connect.sid cookie is httpOnly so we
        // can't read it from JS, but the browser sends it automatically.
        const response = await axios.get('/api/auth/me', { withCredentials: true })

        if (response.data.success && response.data.user) {
          this.user = response.data.user
          this.isAuthenticated = true
          this.error = null

          // If Vuex says not logged in (e.g. localStorage cleared after deploy/new build),
          // restore Vuex from the valid session so authGuard and components work immediately.
          // MUST await so Vuex is synced BEFORE loading=false triggers Login.vue redirect.
          if (this.vuexStore && !this.vuexStore.getters['user/isLoggedIn']) {
            await this.vuexStore.dispatch('user/setUser', this.user)
          }
        } else {
          this.isAuthenticated = false
          this.user = {}
        }
      } catch (e) {
        if (e.response?.status === 401) {
          // Not authenticated — this is normal for anonymous visitors
          this.isAuthenticated = false
          this.user = {}
        } else {
          console.error('Error checking authentication:', e)
          this.error = e
        }
      } finally {
        this.loading = false
      }
    }
  })

  // Expose on window for /clear diagnostics to read auth state
  window.__vue_auth_instance__ = instance

  return instance
}

// Create a simple Vue plugin to expose the wrapper object throughout the application
// This provides this.$auth to all components, just like Auth0Plugin did
export const SessionAuthPlugin = {
  install (Vue, options = {}) {
    Vue.prototype.$auth = useSessionAuth(options)
    // Note: We don't need a Vue mixin watcher here because we're manually
    // calling vuexStore.dispatch in the SessionAuth created() hook.
    // This ensures Vuex is updated before the app renders.
  }
}
